/*
 * UNIK explicit size selection on Colorblock's product page (F2-02).
 *
 * Colorblock always has a selected variant. With the UNIK purchase block, the size
 * option is rendered unchecked (snippets/product-variant-options.liquid, "UNIK: F2-02")
 * and <unik-size-gate> keeps the customer's own size choice:
 *  - records the chosen size when a size input changes;
 *  - clears the choice (and announces it) when another option makes the chosen size a
 *    combination that does not exist, before Colorblock requests the new variant;
 *  - re-applies the choice after Colorblock replaces <variant-selects>;
 *  - while no size is chosen, briefly checks an available size of the new option value
 *    while Colorblock collects the selected values (F2-04). Shopify otherwise fills the
 *    missing size with the first size, which can be sold out or not exist, and the add to
 *    cart button would turn into "Utsolgt"/"Utilgjengelig" instead of opening the dialog;
 *  - while no size is chosen, marks other option values as unavailable only when no
 *    size of that value is available (Colorblock would judge them against the size it
 *    picked itself).
 *
 * Size dialog (F2-03): while no size is chosen, submitting the product form (button,
 * Enter or requestSubmit) is stopped in the capture phase, before Colorblock's
 * product-form sees it, and the size dialog (snippets/unik-size-dialog.liquid) opens.
 * Choosing a size in the dialog clicks the real size input, so Colorblock fetches the
 * variant as usual; the dialog's button then submits the real form once. Its size guide
 * link opens the product's size guide (F4-01) on top when there is one.
 *
 * While Colorblock loads the variant after an option change, the add to cart button stays
 * usable (product-info.js, "UNIK: F6-03"). A submit in that time is held: the button shows
 * Colorblock's spinner and the form is submitted once the variant is in, unless it is sold
 * out or unavailable (F6-03).
 * Requires assets/unik-purchase-rules.js (window.UnikPurchaseRules).
 */
(() => {
  if (customElements.get('unik-size-gate')) return;

  class UnikSizeGate extends HTMLElement {
    constructor() {
      super();
      this.chosen = null;
      this.observedParent = null;
      this.onChange = this.onChange.bind(this);
      this.onChangeCollected = this.onChangeCollected.bind(this);
      this.heldSize = null;
      this.onSubmit = this.onSubmit.bind(this);
      this.observer = new MutationObserver(() => this.sync());
      this.trigger = null;
      this.backdropPress = false;
      this.variantLoading = false;
      this.heldSubmit = false;
      this.loadingTimer = 0;
    }

    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (!this.productInfo) return;
      this.sizeIndex = Number(this.dataset.sizePosition) - 1;
      this.variants = this.readVariants();
      this.productInfo.addEventListener('change', this.onChange, true);
      this.productInfo.addEventListener('change', this.onChangeCollected);
      this.productInfo.addEventListener('submit', this.onSubmit, true);
      this.setUpDialog();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId && event.data.sectionId !== (this.productInfo.sectionId || this.productInfo.dataset.section)) return;
          this.onVariantLoaded();
          this.renderDialog();
        });
      }
      this.sync();
      // Outfit changes the label widths when it has loaded.
      document.fonts?.ready.then(() => this.fitTiles());
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener('change', this.onChange, true);
      this.productInfo?.removeEventListener('change', this.onChangeCollected);
      this.productInfo?.removeEventListener('submit', this.onSubmit, true);
      this.unsubscribeVariantChange?.();
      window.clearTimeout(this.loadingTimer);
      this.observer.disconnect();
      this.observedParent = null;
    }

    /** True while the customer has not chosen a size. Used by the purchase dialog (F2-03). */
    get isSizePending() {
      return this.chosen === null;
    }

    get chosenSize() {
      return this.chosen ? { ...this.chosen } : null;
    }

    readVariants() {
      try {
        return JSON.parse(this.querySelector('[data-unik-variants]')?.textContent || '[]');
      } catch {
        return [];
      }
    }

    /** The visible <variant-selects>. While Colorblock swaps it, the old one is hidden for 500 ms. */
    get variantSelects() {
      return Array.from(this.productInfo.querySelectorAll('variant-selects')).find((element) => element.style.display !== 'none') || null;
    }

    sizeFieldset(variantSelects = this.variantSelects) {
      return variantSelects?.querySelector('[data-unik-size-option]') || null;
    }

    optionGroups(variantSelects) {
      return Array.from(variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'));
    }

    /** Selected value per option position; the size counts as unselected. */
    selectedOptions(variantSelects) {
      return this.optionGroups(variantSelects).map((group, index) => {
        if (index === this.sizeIndex) return null;
        const select = group.querySelector('select');
        if (select) return select.value || null;
        return group.querySelector('input:checked')?.value ?? null;
      });
    }

    onChange(event) {
      const variantSelects = this.variantSelects;
      const input = event.target;
      if (!variantSelects || !variantSelects.contains(input)) return;
      this.startVariantLoading();

      if (input.closest('[data-unik-size-option]')) {
        if (input.disabled) return;
        this.chosen = { id: input.dataset.optionValueId, value: input.value };
        this.announce('');
        this.render(variantSelects);
        return;
      }

      if (this.chosen) {
        const { sizeState } = window.UnikPurchaseRules;
        const state = sizeState(this.variants, this.selectedOptions(variantSelects), this.sizeIndex, this.chosen.value);
        if (state.exists) return;

        // Clear the size before Colorblock's VariantSelects (bubble phase) collects the
        // selected values, so the request asks for a variant that exists.
        this.sizeFieldset(variantSelects)
          ?.querySelectorAll('input[data-unik-size-value]')
          .forEach((sizeInput) => {
            sizeInput.checked = false;
          });
        this.chosen = null;
        this.announce(this.dataset.resetMessage || '');
        this.render(variantSelects);
      }
      this.holdAvailableSize(variantSelects);
    }

    /** Checks the first available (else existing) size for the other selected options until Colorblock has read them. */
    holdAvailableSize(variantSelects) {
      const { sizeState } = window.UnikPurchaseRules;
      const selected = this.selectedOptions(variantSelects);
      const inputs = Array.from(this.sizeFieldset(variantSelects)?.querySelectorAll('input[data-unik-size-value]') || []);
      const states = inputs.map((candidate) => sizeState(this.variants, selected, this.sizeIndex, candidate.value));
      const input = inputs[states.findIndex((state) => state.available)] || inputs[states.findIndex((state) => state.exists)];
      if (!input) return;
      input.checked = true;
      this.heldSize = input;
    }

    /** Bubble phase on product-info: Colorblock's VariantSelects has read the values by now. */
    onChangeCollected() {
      if (!this.heldSize) return;
      this.heldSize.checked = false;
      this.heldSize = null;
    }

    /** Re-applies state after Colorblock replaced <variant-selects> (and on first load). */
    sync() {
      const variantSelects = this.variantSelects;
      if (!variantSelects) return;
      this.observe(variantSelects.parentElement);

      const fieldset = this.sizeFieldset(variantSelects);
      if (!fieldset) return;
      const inputs = Array.from(fieldset.querySelectorAll('input[data-unik-size-value]'));

      if (this.chosen) {
        const input = inputs.find((candidate) => candidate.value === this.chosen.value);
        if (input && !input.disabled) {
          if (!input.checked) input.checked = true;
        } else {
          this.chosen = null;
          this.announce(this.dataset.resetMessage || '');
        }
      }
      if (!this.chosen) {
        // Also undoes form state restored by the browser on reload/back navigation.
        inputs.forEach((input) => {
          if (input.checked) input.checked = false;
        });
      }
      this.render(variantSelects);
      this.fitTiles(fieldset);
    }

    /**
     * Equal size tiles, at least as wide as the widest unbroken label part (as in the Shella
     * reference). Set on <product-info>, so the page tiles and the size dialog share it.
     */
    fitTiles(fieldset = this.sizeFieldset()) {
      const label = fieldset?.querySelector('input[data-unik-size-value] + label');
      if (!label) return;
      const style = getComputedStyle(label);
      const frame = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth']
        .reduce((sum, property) => sum + (parseFloat(style[property]) || 0), 0);
      const parts = Array.from(fieldset.querySelectorAll('.unik-size-value__part'), (part) => part.getBoundingClientRect().width);
      const widest = Math.max(0, ...parts);
      if (!widest) return;
      const value = `${Math.ceil(widest + frame)}px`;
      if (this.productInfo.style.getPropertyValue('--unik-size-tile-width') !== value) {
        this.productInfo.style.setProperty('--unik-size-tile-width', value);
      }
    }

    observe(parent) {
      if (!parent || parent === this.observedParent) return;
      this.observer.disconnect();
      this.observer.observe(parent, { childList: true });
      this.observedParent = parent;
    }

    render(variantSelects) {
      const fieldset = this.sizeFieldset(variantSelects);
      const label = fieldset?.querySelector('[data-unik-size-label]');
      if (label) {
        const text = this.chosen ? this.chosen.value : label.dataset.unselected || '';
        if (label.textContent !== text) label.textContent = text;
      }
      this.toggleAttribute('data-size-pending', this.isSizePending);
      this.productInfo.toggleAttribute('data-unik-size-pending', this.isSizePending);
      if (this.isSizePending) this.markOtherOptionsBySizeWildcard(variantSelects);
      this.renderDialog();
    }

    markOtherOptionsBySizeWildcard(variantSelects) {
      const { sizeState } = window.UnikPurchaseRules;
      const selected = this.selectedOptions(variantSelects);
      this.optionGroups(variantSelects).forEach((group, index) => {
        if (index === this.sizeIndex) return;
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const { available } = sizeState(this.variants, selected, index, input.value);
          const className = input.classList.contains('swatch-input__input') ? 'visually-disabled' : 'disabled';
          input.classList.toggle(className, !available);
        });
      });
    }

    // --- Size dialog (F2-03) -----------------------------------------------------------

    get dialog() {
      return this.querySelector('[data-unik-size-dialog]');
    }

    get form() {
      return this.productInfo.querySelector(`form#${CSS.escape(this.dataset.productFormId || '')}`);
    }

    get submitButton() {
      return this.form?.querySelector('[type="submit"]') || null;
    }

    get submitBlocked() {
      const button = this.submitButton;
      return !button || button.disabled || button.getAttribute('aria-disabled') === 'true';
    }

    setUpDialog() {
      const dialog = this.dialog;
      if (!dialog || dialog.dataset.unikReady) return;
      dialog.dataset.unikReady = 'true';
      dialog.querySelector('[data-unik-size-dialog-close]')?.addEventListener('click', () => this.closeDialog());
      dialog.querySelector('[data-unik-size-dialog-submit]')?.addEventListener('click', () => this.submitFromDialog());
      // F4-01: the guide opens on top of the size dialog, which is still open when the
      // guide closes. Without a guide block (or JS) the link opens the guide page.
      dialog.querySelector('[data-unik-size-dialog-guide]')?.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (window.UnikSizeGuide?.open(this.productInfo, event.currentTarget)) event.preventDefault();
      });
      dialog.addEventListener('close', () => this.onDialogClose());
      // Close on a press that starts and ends on the backdrop (outside the dialog box).
      const outside = (event) => {
        if (event.target !== dialog) return false;
        const rect = dialog.getBoundingClientRect();
        return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      };
      dialog.addEventListener('pointerdown', (event) => {
        this.backdropPress = outside(event);
      });
      dialog.addEventListener('click', (event) => {
        if (this.backdropPress && outside(event)) this.closeDialog();
        this.backdropPress = false;
      });
    }

    onSubmit(event) {
      if (event.target !== this.form) return;
      if (this.isSizePending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openDialog(event.submitter || this.submitButton);
        return;
      }
      if (!this.variantLoading) return;
      // The form still holds the previous variant: hold the submit until the new one is in.
      event.preventDefault();
      event.stopImmediatePropagation();
      this.heldSubmit = true;
      this.showLoading(true);
    }

    /** Colorblock is fetching the variant for a changed option (product-info.js). */
    startVariantLoading() {
      if (typeof this.productInfo.renderProductInfo !== 'function') return;
      this.variantLoading = true;
      window.clearTimeout(this.loadingTimer);
      // Colorblock publishes nothing when the request fails: give up on a held submit then,
      // and say so, or the customer may think the item went into the cart.
      this.loadingTimer = window.setTimeout(() => {
        const held = this.heldSubmit;
        this.variantLoading = false;
        this.heldSubmit = false;
        this.showLoading(false);
        if (held) this.showError(this.dataset.loadFailedMessage || '');
      }, 10000);
    }

    /** Colorblock's error line under the buttons (role="alert"; cleared on the next submit), else the status region. */
    showError(message) {
      const productForm = this.form?.closest('product-form');
      if (typeof productForm?.handleErrorMessage === 'function' && !productForm.hideErrors) productForm.handleErrorMessage(message);
      else this.announce(message);
    }

    onVariantLoaded() {
      if (!this.variantLoading) return;
      this.variantLoading = false;
      window.clearTimeout(this.loadingTimer);
      if (!this.heldSubmit) return;
      this.heldSubmit = false;
      this.showLoading(false);
      if (this.isSizePending || this.submitBlocked) return;
      this.form.requestSubmit(this.submitButton);
    }

    /** Colorblock's own loading state of the add to cart button: the label turns into its spinner. */
    showLoading(on) {
      const button = this.submitButton;
      if (!button) return;
      button.classList.toggle('loading', on);
      button.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
      if (on) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }

    openDialog(trigger) {
      const dialog = this.dialog;
      if (!dialog || dialog.open) return;
      this.trigger = trigger || document.activeElement;
      this.renderDialog(true);
      dialog.showModal();
      const first = dialog.querySelector('[data-unik-size-dialog-options] button:not([disabled])') || dialog.querySelector('[data-unik-size-dialog-close]');
      first?.focus();
    }

    closeDialog() {
      if (this.dialog?.open) this.dialog.close();
    }

    onDialogClose() {
      const trigger = this.trigger;
      this.trigger = null;
      if (trigger?.isConnected) trigger.focus();
    }

    submitFromDialog() {
      if (this.isSizePending || this.submitBlocked) return;
      const form = this.form;
      const button = this.submitButton;
      this.closeDialog();
      form.requestSubmit(button);
    }

    /** Rebuilds the dialog's size buttons from the live size inputs and updates its button. */
    renderDialog(force = false) {
      const dialog = this.dialog;
      if (!dialog || (!dialog.open && !force)) return;
      const container = dialog.querySelector('[data-unik-size-dialog-options]');
      const fieldset = this.sizeFieldset();
      const focusedValue = dialog.contains(document.activeElement) ? document.activeElement.dataset.value : undefined;

      const buttons = Array.from(fieldset?.querySelectorAll('input[data-unik-size-value]') || []).map((input) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'unik-size-dialog__option';
        button.dataset.value = input.value;
        button.setAttribute('aria-pressed', String(input.checked));
        const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        const value = label?.querySelector('.unik-size-value');
        if (value) button.append(value.cloneNode(true));
        else button.textContent = input.value;
        if (label) button.setAttribute('aria-label', label.textContent.replace(/\s+/g, ' ').trim());
        if (input.disabled) button.disabled = true;
        if (input.hasAttribute('data-unik-sold-out')) button.setAttribute('data-unik-sold-out', '');
        button.addEventListener('click', () => this.chooseFromDialog(input.value));
        return button;
      });
      container.replaceChildren(...buttons);
      if (focusedValue !== undefined) container.querySelector(`button[data-value="${CSS.escape(focusedValue)}"]`)?.focus();

      const submit = dialog.querySelector('[data-unik-size-dialog-submit]');
      const realLabel = this.submitButton?.querySelector('span')?.textContent.trim();
      if (this.isSizePending) {
        submit.textContent = dialog.dataset.labelSelect;
        submit.disabled = true;
      } else {
        submit.textContent = this.submitBlocked ? realLabel || dialog.dataset.labelUnavailable : dialog.dataset.labelAdd;
        submit.disabled = this.submitBlocked;
      }
    }

    chooseFromDialog(value) {
      const input = Array.from(this.sizeFieldset()?.querySelectorAll('input[data-unik-size-value]') || []).find((candidate) => candidate.value === value);
      if (!input || input.disabled) return;
      if (!input.checked) input.click();
      this.renderDialog();
    }

    announce(message) {
      const status = this.querySelector('[data-unik-size-status]');
      if (!status) return;
      status.textContent = '';
      if (message) window.setTimeout(() => (status.textContent = message), 50);
    }
  }

  customElements.define('unik-size-gate', UnikSizeGate);
})();
